import useAPI from "@toothfairy/shared-api/useApi";
import AppStateManager from "@toothfairy/shared-ui/AppStateManager";
import AppToast from "@toothfairy/shared-ui/AppToast";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import React, { Suspense, useCallback, useEffect, useRef, useState } from "react";
import envConfig from "../../../envConfig";
import { useHistory } from "../../Router";
import ArenaScreen from "./ArenaScreen";
import chatsHelper from "../../API/Chats/chatsHelper";
import useWorkspaces from "../../Hooks/useWorkspaces";
import messagesHelper from "../../API/Messages/messagesHelper";
import Files from "../../API/Files";
import AppUser from "@toothfairy/shared-ui/AppUser";
import { useTranslation } from "react-i18next";
import useAgentStream from "../../Hooks/useAgentStream";

const AgentScreenContainer = ({
  agentId,
  workspaceId,
  setWinner,
  isWinner,
  onChatCreated,
  _inputText,
  onStreamFinished,
  _agentName,
  setAgent,
  isFirstMessage,
  setIsFirstMessage,
  chatId_prop,
  _fileKeys,
  _imageKeys,
  _audioKeys,
  onCancelRef,
}) => {
  const { t } = useTranslation();
  const defaultPlaceholderMessageOnLoading = "**...**";
  const [chatId, setChatId] = useState(null);
  const history = useHistory();
  const { getAgent, getActiveWorkspace } = useWorkspaces();
  const { state, dispatch } = AppStateManager.useAppStateManager();
  const AppToasty = AppToast.useToast();
  const [isLoading, setIsLoading] = useState(false);
  const { user } = AppUser.useUser();
  const [workspace, setWorkspace] = useState(null);
  const [allowStop, setAllowStop] = useState(true);
  const [isAIStreaming, setIsAIStreaming] = useState(false);
  const [isMessagesLoaded, setIsMessagesLoaded] = useState(false);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [messageIndex, setMessageIndex] = useState(1);
  const [inputText, setInputText] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingMessageId, setLoadingMessageId] = useState(null);
  const [agentData, setAgentData] = useState(null);
  const [agentImage, setAgentImage] = useState(null);
  const [agentLogo, setAgentLogo] = useState(null);
  const [waitingForFirstResponse, setWaitingForFirstResponse] = useState(false);
  const [waitingForFirstChunk, setWaitingForFirstChunk] = useState(false);
  const isSSEStreamingRef = useRef(false);
  const { mode } = AppTheme.useTheme();
  const cursor = mode === "light" ? "\u26AB" : "\u26AA";

  const {
    startStream: startSSEStream,
    cancelStream: cancelSSEStream,
    isStreaming: isSSEStreaming,
    getAccumulatedText,
  } = useAgentStream();

  useEffect(() => {
    isSSEStreamingRef.current = isSSEStreaming;
  }, [isSSEStreaming]);

  const chatIdRef = useRef(chatId_prop);
  useEffect(() => {
    chatIdRef.current = chatId_prop;
  }, [chatId_prop]);

  const {
    data: S3downloadUrlData,
    loading: urldownloadGenerationInProgress,
    apiRequest: S3downloadUrlRequest,
    response: S3downloadResponse,
  } = useAPI(Files.downloadUrlGeneration, envConfig);
  const {
    data: S3downloadUrlDataForWidget,
    loading: urldownloadGenerationInProgressForWidget,
    apiRequest: S3downloadUrlRequestForWidget,
    response: S3downloadResponseForWidget,
  } = useAPI(Files.downloadUrlGeneration, envConfig);
  const fetchAgentImage = async (path, isLargeLogo) => {
    const _url = await S3downloadUrlRequest({
      filename: `imported-image/${workspaceId}/${path}`,
      context: "pdf",
      workspaceid: workspaceId,
    });
    if (isLargeLogo) setAgentLogo(_url?.url);
    else setAgentImage(_url?.url);
  };

  useEffect(() => {
    if (agentData?.icon && workspaceId) {
      fetchAgentImage(agentData?.icon);
    } else {
      setAgentImage(null);
    }
  }, [agentData?.icon, workspaceId]);

  useEffect(() => {
    if (agentData?.largeLogo && workspaceId)
      fetchAgentImage(agentData?.largeLogo, true);
  }, [agentData?.largeLogo, workspaceId]);

  useEffect(() => {
    if (agentId) {
      const agent = getAgent(agentId);
      setAgentData(agent);
    }
  }, [agentId]);

  const createChat = async () => {
    if (isFirstMessage) {
      try {
        const result = await chatsHelper.createChat({
          primaryRole: agentId,
          workspaceID: workspaceId,
          allowMultipleRoles: false,
          creationTime: Math.floor(new Date().getTime() / 1000),
          visibility: "widget",
          name: t("arena.newChat"),
        });
        const agent = getAgent(agentId);
        setAgentData(agent);
        onChatCreated(result?.data?.createChat?.id);
        setChatId(result?.data?.createChat?.id);
        setIsFirstMessage(false);
      } catch (error) {
        history.push("/error");
        console.error(error);
      }
    }
  };

  function removeSpecificUnicodeAtEnd(text) {
    const unicodeRegex = /(\u26AB|\u26AA)$/g;
    return text.replace(unicodeRegex, "");
  }

  useEffect(() => {
    const updateMessage = () => {
      setMessages((prevMessages) => {
        let updatedMessages = [...prevMessages];
        updatedMessages[0]["text"] = agentData?.messagesOnLaunch[carouselIndex];
        return updatedMessages;
      });
      setCarouselIndex(
        (prevIndex) =>
          (prevIndex + 1) % (agentData?.messagesOnLaunch.length || 1)
      );
    };
    if (
      agentData?.messagesOnLaunch?.length > 0 &&
      agentData?.rotateMessagesOnLaunchInterval &&
      messages?.length == 1
    ) {
      const interval = setInterval(
        updateMessage,
        agentData?.rotateMessagesOnLaunchInterval || 5000
      );
      return () => {
        clearInterval(interval);
      };
    }
  }, [agentData?.messagesOnLaunch?.length, carouselIndex, messages?.length]);

  const handleFeedback = async (id, humanFeedback) => {
    try {
      const result = await messagesHelper.updateChatMessage(id, [
        {
          property: "humanFeedback",
          value: humanFeedback,
        },
      ]);
      setMessages((prevMessages) => {
        let updatedMessages = [...prevMessages];
        const newMessageIndex = updatedMessages.map((u) => u._id).indexOf(id);
        if (newMessageIndex !== -1) {
          updatedMessages[newMessageIndex] = {
            ...updatedMessages[newMessageIndex],
            humanFeedback: humanFeedback,
          };
        }
        return [...updatedMessages];
      });
      AppToasty.show(t("arena.feedbackSaved"), {
        placement: "bottom",
        type: "success",
      });
    } catch (error) {
      console.log(error);
      AppToasty.show(t("arena.feedbackError"), {
        placement: "bottom",
        type: "danger",
      });
    }
  };

  useEffect(() => {
    if (isFirstMessage && agentId && workspaceId) createChat();
  }, [isFirstMessage, agentId, workspaceId]);

  useEffect(() => {
    if (
      messages.length === 0 &&
      agentData?.id &&
      agentData?.showSplashWhenEmpty !== true
    ) {
      const timer = setTimeout(() => {
        setMessages((prevMessages) => [
          {
            _id: 2,
            text: agentData?.messageOnLaunch || t("arena.hiMessage"),
            createdAt: new Date().getTime() / 1000,
            user: {
              name: "assistant",
              _id: 2,
              agentName: _agentName,
            },
          },
          ...prevMessages,
        ]);
        setMessageIndex((prevIndex) => prevIndex + 1);
      }, 50);
      return () => clearTimeout(timer);
    } else {
      setTimeout(() => {
        setIsMessagesLoaded(true);
      }, 10);
    }
  }, [
    agentData?.messageOnLaunch,
    agentData?.id,
    agentData?.showSplashWhenEmpty,
    messageIndex,
    messages?.length,
  ]);

  const updateMessagesWhileSSEStreaming = useCallback(
    (chunk, messageId, accumulatedText) => {
      setMessages((prevMessages) => {
        const messageIndex = prevMessages.findIndex((m) => m._id === messageId);
        if (messageIndex === -1) return prevMessages;

        const updatedMessages = [...prevMessages];
        const currentMessage = updatedMessages[messageIndex];

        updatedMessages[messageIndex] = {
          ...currentMessage,
          text:
            accumulatedText + (isSSEStreamingRef.current ? " " + cursor : ""),
          loading: false,
        };

        return updatedMessages;
      });
    },
    [cursor],
  );

  const onCancelGeneration = async () => {
    if (isSSEStreaming) {
      cancelSSEStream();
      setIsAIStreaming(false);
      setAllowStop(false);
      setWaitingForFirstResponse(false);
      setWaitingForFirstChunk(false);
      if (loadingMessageId) {
        await messagesHelper.updateChatMessage(loadingMessageId, [
          { property: "status", value: "cancelled" },
        ]);
        const accumulatedText = getAccumulatedText();
        if (accumulatedText) {
          setMessages((prevMessages) => {
            const messageIndex = prevMessages.findIndex(
              (m) => m._id === loadingMessageId,
            );
            if (messageIndex === -1) return prevMessages;
            const updatedMessages = [...prevMessages];
            updatedMessages[messageIndex] = {
              ...updatedMessages[messageIndex],
              text: removeSpecificUnicodeAtEnd(accumulatedText),
              loading: false,
            };
            return updatedMessages;
          });
        }
      }
      await chatsHelper.updateChat(chatIdRef.current, [
        { property: "isAIReplying", value: false },
        { property: "activeMessageForAI", value: null },
      ]);
      setLoadingMessageId(null);
      setTimeout(() => {
        setAllowStop(true);
      }, 1000);
      return;
    }
  };

  useEffect(() => {
    if (onCancelRef) {
      onCancelRef.current = onCancelGeneration;
    }
  }, [onCancelGeneration]);

  useEffect(() => {
    if (agentId && workspaceId && _inputText != null && _inputText != "") {
      fetchData(_inputText);
    }
  }, [agentId, workspaceId, _inputText]);

  const fetchData = async (message) => {
    if (!chatIdRef.current) {
      console.warn("[SSE Arena] No chatId yet, skipping fetchData");
      return;
    }
    try {
      const optimisticMessageId = Math.random()
        .toString(36)
        .substring(7);
      setMessages((previousMessages) => [
        ...previousMessages,
        {
          _id: optimisticMessageId,
          text: message,
          createdAt: new Date().getTime() / 1000,
          user: {
            name: "user",
          },
          loading: false,
          images: _imageKeys ? _imageKeys : null,
          audios: _audioKeys ? _audioKeys : null,
          files: _fileKeys ? _fileKeys : null,
        },
      ]);

      setAllowStop(true);
      setIsAIStreaming(true);
      setWaitingForFirstResponse(true);
      setWaitingForFirstChunk(false);

      let assistantMessageId = null;
      const currentChatId = chatIdRef.current;

      startSSEStream(
        {
          chatid: currentChatId,
          workspaceid: getActiveWorkspace()?.id,
          token: getActiveWorkspace()?.workspaceToken,
          messages: [
            {
              text: message,
              role: "user",
              userID: user?._id,
              images: _imageKeys ? _imageKeys : null,
              audios: _audioKeys ? _audioKeys : null,
              files: _fileKeys ? _fileKeys : null,
            },
          ],
          agentid: agentId,
        },
        {
          onToken: (chunk, messageId, accumulatedText) => {
            setWaitingForFirstResponse(false);
            setWaitingForFirstChunk(false);
            if (messageId && !assistantMessageId) {
              assistantMessageId = messageId;
              setLoadingMessageId(messageId);
              setMessages((previousMessages) => {
                if (previousMessages.find((m) => m._id === messageId)) {
                  return previousMessages;
                }
                let f_date = new Date();
                f_date.setSeconds(f_date.getSeconds() + 1);
                return [
                  ...previousMessages,
                  {
                    _id: messageId,
                    text: null,
                    createdAt: f_date.getTime() / 1000,
                    user: {
                      _id: 2,
                      name: "assistant",
                      agentName: _agentName,
                    },
                    loading: true,
                  },
                ];
              });
            }
            updateMessagesWhileSSEStreaming(
              chunk,
              messageId || assistantMessageId,
              accumulatedText,
            );
          },
          onMessage: (data) => {
            if (data.type === "chat_created" && data.message_id) {
              assistantMessageId = data.message_id;
              setLoadingMessageId(data.message_id);
              setWaitingForFirstResponse(false);
              setWaitingForFirstChunk(true);
              setMessages((previousMessages) => {
                if (previousMessages.find((m) => m._id === data.message_id)) {
                  return previousMessages;
                }
                let f_date = new Date();
                f_date.setSeconds(f_date.getSeconds() + 1);
                return [
                  ...previousMessages,
                  {
                    _id: data.message_id,
                    text: null,
                    createdAt: f_date.getTime() / 1000,
                    user: {
                      _id: 2,
                      name: "assistant",
                      agentName: _agentName,
                    },
                    loading: true,
                  },
                ];
              });
            }

            if (data.status === "fulfilled" && data.type === "message") {
              setIsAIStreaming(false);
              setAllowStop(false);
              setLoadingMessageId(null);
              if (data.message_id || assistantMessageId) {
                const msgId = data.message_id || assistantMessageId;
                setMessages((prevMessages) => {
                  const messageIndex = prevMessages.findIndex(
                    (m) => m._id === msgId,
                  );
                  if (messageIndex === -1) return prevMessages;

                  const updatedMessages = [...prevMessages];
                  const currentText =
                    updatedMessages[messageIndex].text || "";
                  updatedMessages[messageIndex] = {
                    ...updatedMessages[messageIndex],
                    text: removeSpecificUnicodeAtEnd(currentText),
                    loading: false,
                    metadata: data.metadata
                      ? typeof data.metadata === "string"
                        ? JSON.parse(data.metadata)
                        : data.metadata
                      : updatedMessages[messageIndex].metadata,
                    callbackMetadata: data.callbackMetadata
                      ? typeof data.callbackMetadata === "string"
                        ? JSON.parse(data.callbackMetadata)
                        : data.callbackMetadata
                      : updatedMessages[messageIndex].callbackMetadata,
                    files:
                      data.files || updatedMessages[messageIndex].files,
                    images:
                      data.images || updatedMessages[messageIndex].images,
                    audios:
                      data.audios || updatedMessages[messageIndex].audios,
                  };
                  return updatedMessages;
                });
              }
              chatsHelper.updateChat(currentChatId, [
                { property: "isAIReplying", value: false },
                { property: "activeMessageForAI", value: null },
              ]);
            }
          },
          onComplete: async (finalText) => {
            setIsAIStreaming(false);
            setAllowStop(false);
            setWaitingForFirstResponse(false);
            setWaitingForFirstChunk(false);
            if (assistantMessageId) {
              setMessages((prevMessages) => {
                const messageIndex = prevMessages.findIndex(
                  (m) => m._id === assistantMessageId,
                );
                if (messageIndex === -1) return prevMessages;

                const updatedMessages = [...prevMessages];
                updatedMessages[messageIndex] = {
                  ...updatedMessages[messageIndex],
                  text: removeSpecificUnicodeAtEnd(finalText),
                  loading: false,
                };
                return updatedMessages;
              });
            }
            await chatsHelper.updateChat(currentChatId, [
              { property: "isAIReplying", value: false },
              { property: "activeMessageForAI", value: null },
            ]);
            onStreamFinished();
          },
          onError: async (error) => {
            console.error("SSE Arena stream error:", error);
            setIsAIStreaming(false);
            setAllowStop(false);
            setWaitingForFirstResponse(false);
            setWaitingForFirstChunk(false);
            AppToasty.show(t("arena.feedbackError"), {
              placement: "bottom",
              type: "danger",
            });
            await chatsHelper.updateChat(currentChatId, [
              { property: "isAIReplying", value: false },
              { property: "activeMessageForAI", value: null },
            ]);
            onStreamFinished();
          },
        },
      );
    } catch (error) {
      console.error(error);
      setIsAIStreaming(false);
      onStreamFinished();
    }
  };

  const lastMessageFromAssistant =
    messages?.filter((u) => u?.user?.name === "assistant")?.slice(-1)[0] || {};

  useEffect(() => {
    if (
      messages?.filter((u) => u?.loading === true).length > 0 &&
      !isAIStreaming
    ) {
      setMessages((previousMessages) => {
        const updatedMessages = previousMessages.slice();
        updatedMessages[0].loading = false;
        return updatedMessages;
      });
    }
  }, [messages?.length, isAIStreaming]);

  const handleSend = (message) => {
    if (message.trim().length === 0) return;
    setIsLoading(true);
    fetchData(message);
  };
  const onQuestionPress = (question) => {
    if (!isLoading) handleSend(question);
  };

  return (
    <Suspense fallback={<div>{t("arena.loading")}</div>}>
      {agentId && urldownloadGenerationInProgress == false ? (
        <ArenaScreen
          setAgent={setAgent}
          agentId={agentId}
          setWinner={setWinner}
          isWinner={isWinner}
          S3downloadUrlRequest={S3downloadUrlRequestForWidget}
          onQuestionPress={onQuestionPress}
          messages={messages}
          setMessages={setMessages}
          user={user}
          handleSend={handleSend}
          isMessagesLoaded={isMessagesLoaded}
          languages={state.languages}
          selectedLanguage={state.selectedLanguage}
          isAIStreaming={isAIStreaming}
          onCancel={onCancelGeneration}
          inputText={inputText}
          setInputText={setInputText}
          loadingMessageId={loadingMessageId}
          isLastMessageEmpty={lastMessageFromAssistant?.text == ""}
          allowFeedback={agentData?.allowFeedback}
          handleFeedback={handleFeedback}
          isLoading={isLoading}
          placeholder={agentData?.placeholderInputMessage || t("arena.placeholder")}
          agentTitle={agentData?.label}
          textToDisplayWhileLoading={
            agentData?.placeholderMessageOnLoading ||
            defaultPlaceholderMessageOnLoading
          }
          showSplash={agentData?.showSplashWhenEmpty}
          splashTitle={agentData?.messageOnLaunch}
          coder={true}
          isAgentFetched={agentData?.label != null}
          disclaimer={agentData?.disclaimer}
          agentImage={agentImage}
          agentLogo={agentLogo}
          agentColor={mode === "dark" ? agentData?.darkColor : agentData?.color}
          logoBackground={
            mode === "dark"
              ? agentData?.darkLogoBackground
              : agentData?.logoBackground
          }
          showTimeForResponse={agentData?.showTimeForResponse}
          showDetectedLanguage={agentData?.showDetectedLanguage}
          quickQuestions={agentData?.quickQuestions
            ?.split(";")
            ?.map((u) => u?.trim()?.replace(/\s+/g, " "))
            ?.filter((u) => u != "")}
          allowStop={allowStop}
          workspaceId={workspaceId}
          workspace={workspace}
          _agentName={_agentName}
        />
      ) : (
        <div className="tf-loader"></div>
      )}
    </Suspense>
  );
};

export default AgentScreenContainer;
